import { Modal } from 'antd';
import React, { FC, useState } from 'react';
import styles from './LocationModal.module.scss';
import { Form, Formik } from 'formik';
import clsx from 'clsx';
import { Address } from 'models/Address/address.model';
import fetchAddressFromGeoCode from 'shared/utils/fetchAddressFromGeoCode';
import COButton from 'shared/components/COButton';
import Map from 'shared/components/map';
import InputField from 'shared/components/InputField';
import { categoryList } from '../../companySetupConstants';
import COSelect from 'shared/components/COSelect';
import { fetchGeocodeData } from 'shared/utils/fetchGeoLocation';
import { locationValidationSchema } from 'views/CompanySetup/validation';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import useRedirect from 'shared/utils/useRedirect';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { modalCloseIcon } from 'shared/components/OrganizationModal';
interface LocationModalProps {
  showModal: boolean;
  onCancel: () => void;
  handleToolTipOpen?: (fieldName?: HelpDeskInputFieldEnum) => () => void;
}

const LocationModal: FC<LocationModalProps> = props => {
  const { showModal, onCancel, handleToolTipOpen } = props;

  const { redirectToActivityDataEntry } = useRedirect();
  const { organizationData } = useParentOrganizationContext();
  const { createFirstLocation, loading } = ParentCompanySetupService();
  const searchPlaceByCountryProvince = async (
    country: string,
    province: string,
    setFieldValue: Function
  ) => {
    try {
      setFetchingAddress(true);
      const results = await fetchGeocodeData(`${province} ${country}`);
      if (results) {
        setFieldValue('map', results?.formattedAddress);
        setAddressInState(
          results?.addressComponents,
          results?.latLng,
          setFieldValue
        );
      }
    } catch (error) {
    } finally {
      setFetchingAddress(false);
    }
  };
  const handleOnSave = async (data: Address) => {
    const {
      name,
      category,
      city,
      street,
      province,
      postalCode,
      country,
      countryCode,
      provinceCode,
      latitude,
      longitude,
    } = data;

    const payload = {
      data: {
        location: {
          name,
          category,
          organization: {
            id: organizationData?.organization?.id,
          },
          address: {
            city,
            street,
            province,
            postalCode: postalCode || null,
            country,
            countryCode: countryCode,
            latitude,
            longitude,
            provinceCode,
          },
        },
      },
    };
    const locId = await createFirstLocation(payload);
    redirectToActivityDataEntry(locId, {
      activeTab: String(organizationData?.company?.baselineYear),
      isEditView: true,
      isFirstFlow: true,
    });
  };
  const setAddressInState = (
    addressComponents: google.maps.GeocoderAddressComponent[],
    latling: google.maps.LatLng | null,
    setFieldValue: Function
  ) => {
    const { streetLocality, country, province, city, pinCode } =
      fetchAddressFromGeoCode(addressComponents);

    if (streetLocality.trim() === '') {
      setFieldValue('street', '');
    } else {
      setFieldValue('street', streetLocality?.trim() ?? '');
    }
    setFieldValue('country', country?.long_name ?? '');
    setFieldValue('countryCode', country?.short_name ?? '');
    setFieldValue('province', province?.long_name ?? '');
    setFieldValue('provinceCode', province?.short_name ?? '');
    setFieldValue('city', city?.long_name ?? '');
    setFieldValue('postalCode', pinCode?.long_name ?? '');
    setFieldValue('latitude', latling?.lat());
    setFieldValue('longitude', latling?.lng());
  };

  const initialValues = {
    name: '',
    category: undefined,
    city: '',
    street: '',
    country: '',
    province: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    countryCode: '',
  };

  const [fetchingAddress, setFetchingAddress] = useState<boolean>(false);

  const { locationName, locationCategory, locationAddress } =
    HelpDeskInputFieldEnum;

  return (
    <Modal
      destroyOnClose={true}
      maskClosable={false}
      width={725}
      zIndex={1000}
      footer={null}
      wrapClassName={styles.modalContainer}
      open={showModal}
      closeIcon={modalCloseIcon}
      onCancel={onCancel}>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <h2>Add New Location</h2>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleOnSave}
            validationSchema={locationValidationSchema}>
            {({ setFieldValue, values }) => (
              <Form>
                <div className={styles.fieldContainer}>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="name"
                      placeholderstring="Enter your location name"
                      label="Location Name"
                      info
                      infoClick={handleToolTipOpen?.(locationName)}
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <COSelect
                      name="category"
                      placeholderstring="Location Category"
                      label="Location Category"
                      options={categoryList}
                      info
                      infoClick={handleToolTipOpen?.(locationCategory)}
                    />
                  </div>
                  <div
                    className={clsx({
                      [styles.inputContainer]: true,
                      [styles.longContainer]: true,
                    })}>
                    <div className={styles.mapContainer}>
                      <Map
                        name="map"
                        setAddress={(
                          addressComponents: google.maps.GeocoderAddressComponent[],
                          latling: google.maps.LatLng | null
                        ) =>
                          setAddressInState(
                            addressComponents,
                            latling,
                            setFieldValue
                          )
                        }
                        latitude={values?.latitude}
                        longitude={values?.longitude}
                        info
                        infoClick={handleToolTipOpen?.(locationAddress)}
                      />
                    </div>
                  </div>
                  <div className={styles.inputContainerLocality}>
                    <InputField
                      name="street"
                      placeholderstring="Street, Locality"
                      label="Street"
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="city"
                      placeholderstring="City"
                      label="City"
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="province"
                      placeholderstring="Province"
                      label="Province"
                      onBlur={() =>
                        searchPlaceByCountryProvince(
                          values.country ?? '',
                          values.province ?? '',
                          setFieldValue
                        )
                      }
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="country"
                      placeholderstring="Country"
                      label="Country"
                      onBlur={() =>
                        searchPlaceByCountryProvince(
                          values.country ?? '',
                          values.province ?? '',
                          setFieldValue
                        )
                      }
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <InputField
                      name="postalCode"
                      placeholderstring="Postal Code"
                      label="Postal Code"
                    />
                  </div>
                </div>

                <div className={styles.buttonContainer}>
                  <div className={styles.singleButtonContainer}>
                    <COButton submit loading={fetchingAddress || loading}>
                      Submit
                    </COButton>
                    <COButton secondary onClick={onCancel} disabled={loading}>
                      Back
                    </COButton>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export default LocationModal;
